import { apiClient } from '$lib/api/client';
import { turnstile } from '$lib/turnstile';

/** One job title a phrase reached, for the customer to keep or discard. */
export interface TitleSuggestion {
  title: string;
  people: number;
  similarity: number;
}

interface TitleSuggestResponse {
  titles: TitleSuggestion[] | null;
}

/**
 * The floor the app asks for.
 *
 * # Why it is not lower
 *
 * A German title is often two words of which one is generic, and the generic
 * one carries the match: at 0.70 "leiter labor" reached `leiter` (245 people),
 * `leiter it` (187) and `leiter service` (114) — a list about seniority rather
 * than about laboratories, where the real lab titles held 38 people between
 * them. Below about 0.78 the suggestions stop being a segment and become a
 * vocabulary, and a customer cannot untick their way out of that.
 *
 * # What it costs
 *
 * Real neighbours, measured: "leiter brandschutz" loses `projektleiter
 * brandschutz` at 0.764 — the largest group in that family, sixteen people —
 * and "leiter qualitätssicherung" loses the bare `qualitätssicherung` at 0.770,
 * a hundred and two. Those are the phrases where German compounding puts a
 * genuine synonym just under the line, and the answer for them is a second
 * search rather than a lower floor for everybody.
 */
export const titleFloor = 0.78;

/**
 * Ask which job titles a phrase reaches.
 *
 * Guarded by Turnstile, unlike the count: this embeds text. The token is
 * fetched the way the filter search fetches it, with the same short timeout —
 * a challenge that hangs must not leave somebody staring at a spinner.
 */
export async function suggestTitles(phrase: string, limit = 40): Promise<TitleSuggestion[]> {
  if (!phrase.trim()) return [];

  let turnstileToken: string | undefined;
  try {
    turnstileToken = await Promise.race([
      turnstile.getToken(),
      new Promise<string>((_, reject) =>
        setTimeout(() => reject(new Error('Turnstile timeout')), 5000)
      )
    ]);
  } catch {
    // Continue without a token; the service decides whether to insist.
  }

  const response = await apiClient.post<TitleSuggestResponse>('/deep-iq/title-suggest', {
    phrase,
    min_similarity: titleFloor,
    limit,
    turnstile_token: turnstileToken
  });

  return response.data.titles ?? [];
}
